import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Check,
  Clock,
  Star,
  TrendingUp,
  Lightbulb,
  Calendar,
} from "lucide-react";
import ScheduleFocusTime from "@/components/ScheduleFocusTime";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useUser } from "@/contexts/UserContext";
import { ActionSuggestion } from "@/types/user";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from "recharts";
import InsightPanel from "@/components/InsightPanel";
import { notificationsService } from "@/services/notificationsService";
import { useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { fetchWeeklyInsights } from "../team/services/insightService";
import EnhancedInsightsPanel from "./EnhancedInsightsPanel";
import { insightsService } from "@/services/insightsService";
import { wellnessService } from "@/services/wellnessService";

export type InsightData = {
  id: string;
  title: string;
  description: string;
  actionType: "focus" | "break" | "connect" | "reflect" | "celebrate";
  priority: "high" | "medium" | "low";
};

export type WeeklyInsightData = {
  tone: {
    positive: number;
    neutral: number;
    negative: number;
  };
  topThemes: Array<{ name: string; count: number }>;
  weeklyChanges: {
    energy: number;
    focus: number;
  };
  focusPatterns: {
    peakTimeRange: string;
    recommendation: string;
  };
  energyTrend: {
    percentageChange: number;
    message: string;
  };
  upcomingActivities: {
    count: number;
    message: string;
  };
  weeklySummary: {
    text: string[];
    recommendation: string;
  };
};

const insights: InsightData[] = [
  {
    id: "1",
    title: "Schedule Focus Time",
    description:
      "Block 60-90 minutes for deep work on your priority task without distractions.",
    actionType: "focus",
    priority: "high",
  },
  {
    id: "2",
    title: "Connect with Team Members",
    description:
      "Several team members have similar goals. Consider scheduling time to collaborate.",
    actionType: "connect",
    priority: "medium",
  },
  {
    id: "3",
    title: "Reflect on Progress",
    description:
      "You've completed several important tasks this week. Take time to document your progress.",
    actionType: "reflect",
    priority: "medium",
  },
  {
    id: "4",
    title: "Take a Break",
    description:
      "You've been working intensely for several hours. Consider a short break to recharge.",
    actionType: "break",
    priority: "low",
  },
];

export default function DashboardInsights() {
  const { user } = useUser();
  const navigate = useNavigate();
  const [weeklyInsightData, setWeeklyInsightData] = useState<WeeklyInsightData | null>(null);
  const [unifiedInsights, setUnifiedInsights] = useState<any[]>([]);


  // Helper function to create baseline insights for users with no check-ins
  const createBaselineInsights = (): WeeklyInsightData => {
    return {
      tone: {
        positive: 0.7,
        neutral: 0.25,
        negative: 0.05,
      },
      topThemes: [
        { name: "Getting Started", count: 1 },
        { name: "Self Awareness", count: 1 },
        { name: "Growth", count: 1 },
      ],
      weeklyChanges: {
        energy: 0,
        focus: 0,
      },
      focusPatterns: {
        peakTimeRange: "Complete check-ins to discover your peak performance times.",
        recommendation: "Start with daily check-ins to identify your optimal focus periods.",
      },
      energyTrend: {
        percentageChange: 0,
        message: "Complete your first check-in to start tracking your energy trends!",
      },
      upcomingActivities: {
        count: 0,
        message: "Ready to begin your growth journey.",
      },
      weeklySummary: {
        text: [
          "Welcome to Step! Your insights will appear here after check-ins.",
          "Track your energy, focus, and mood daily to see patterns emerge.",
          "Set goals and build habits for consistent growth.",
        ],
        recommendation: "Start with your first daily check-in to begin tracking your patterns and unlock personalized insights.",
      },
    };
  };

  // Helper function to create insights for users with 1-4 check-ins
  const createStarterInsights = (checkIns: any[]): WeeklyInsightData => {
    const count = checkIns.length;
    const energyValues = checkIns.map((c) =>
      typeof c.energy === "number" ? c.energy : 0
    );
    const focusValues = checkIns.map((c) =>
      typeof c.focus === "number" ? c.focus : 0
    );

    const avgEnergy = energyValues.length
      ? energyValues.reduce((sum, value) => sum + value, 0) / energyValues.length
      : 0;
    const avgFocus = focusValues.length
      ? focusValues.reduce((sum, value) => sum + value, 0) / focusValues.length
      : 0;

    const earliest = checkIns[checkIns.length - 1];
    const latest = checkIns[0];

    const earliestEnergy = typeof earliest?.energy === "number" ? earliest.energy : avgEnergy;
    const latestEnergy = typeof latest?.energy === "number" ? latest.energy : avgEnergy;
    const earliestFocus = typeof earliest?.focus === "number" ? earliest.focus : avgFocus;
    const latestFocus = typeof latest?.focus === "number" ? latest.focus : avgFocus;

    const energyChange =
      earliestEnergy > 0
        ? Math.round(((latestEnergy - earliestEnergy) / earliestEnergy) * 100)
        : 0;
    const focusChange =
      earliestFocus > 0
        ? Math.round(((latestFocus - earliestFocus) / earliestFocus) * 100)
        : 0;

    const positiveRatio = avgEnergy >= 70 ? 0.75 : avgEnergy >= 50 ? 0.55 : 0.35;

    return {
      tone: {
        positive: positiveRatio,
        neutral: (1 - positiveRatio) * 0.7,
        negative: (1 - positiveRatio) * 0.3,
      },
      topThemes: [
        { name: "Daily Habits", count: count },
        { name: "Energy Tracking", count: count },
        {
          name: avgEnergy >= 70 ? "High Energy" : "Building Momentum",
          count: Math.ceil(count / 2),
        },
      ],
      weeklyChanges: {
        energy: energyChange,
        focus: focusChange,
      },
      focusPatterns: {
        peakTimeRange: count >= 2 
          ? "Patterns developing - keep tracking to identify your peak times." 
          : "Complete more check-ins to identify your focus patterns.",
        recommendation: "Continue daily check-ins to discover your optimal productivity periods.",
      },
      energyTrend: {
        percentageChange: energyChange,
        message:
          avgEnergy >= 70
            ? `Great start! Your energy levels are ${avgEnergy >= 80 ? "strong" : "solid"} - keep building this momentum.`
            : "You're building awareness! Continue tracking to optimize your energy levels.",
      },
      upcomingActivities: {
        count: 1,
        message: "Tomorrow's check-in awaits - keep your streak going!",
      },
      weeklySummary: {
        text: [
          `Excellent progress! You've completed ${count} check-in${count > 1 ? 's' : ''}.`,
          `Your average energy level is ${Math.round(avgEnergy)}%.`,
          count >= 2 ? "You're building a valuable daily habit." : "One check-in down - keep the momentum going!",
        ],
        recommendation: count < 3 
          ? "Continue with daily check-ins to establish patterns and unlock deeper insights."
          : "Great consistency! Ready to set your first weekly goal?",
      },
    };
  };

  useEffect(() => {
    const loadInsights = async () => {
      // Always try to load insights, create baseline for new users
      if (!user?.id) return;

      try {
        // Fetch unified insights (aggregates dashboard insights + weekly insights)
        const unifiedResult = await insightsService.getUnifiedInsights(user.teamId || null).catch(() => null);

        if (unifiedResult) {
          // Set unified insights
          if (unifiedResult.unified && Array.isArray(unifiedResult.unified)) {
            const mappedInsights = unifiedResult.unified.map((insight: any) => ({
              id: insight.id,
              title: insight.title,
              description: insight.description,
              category: insight.category,
              reasoning: insight.reasoning || '',
              priority: insight.priority || 'medium',
              actionable: insight.actionable || false,
              suggestedAction: insight.reasoning || '',
              teamContext: insight.teamContext || null,
              sources: insight.sources || [],
            }));
            setUnifiedInsights(mappedInsights);
          }

          // Set weekly insights from unified response
          if (unifiedResult.weekly) {
            setWeeklyInsightData(unifiedResult.weekly);
          } else {
            setWeeklyInsightData(createBaselineInsights());
          }
        } else {
          // Fallback: use old approach if unified API fails
          const [dashboardInsights, weeklyResult] = await Promise.all([
            insightsService.getDashboardInsights().catch(() => null),
            fetchWeeklyInsights(user.id, user.teamId || null).catch(() => null),
          ]);

          // Set dashboard insights - now returns ActionableInsight[] format directly
          if (dashboardInsights && Array.isArray(dashboardInsights)) {
            // dashboardInsights is already ActionableInsight[] format (matching backend)
            const mappedInsights = dashboardInsights.map((insight: any, index: number) => ({
              id: insight.id || `insight-${index}`,
              title: insight.title,
              description: insight.description || insight.message,
              category: insight.category || insight.type,
              reasoning: insight.reasoning || insight.suggestedAction || '',
              priority: insight.impact || insight.priority || 'medium',
              actionable: true, // ActionableInsight is always actionable
              suggestedAction: insight.suggestedAction,
              teamContext: null,
              sources: [],
            }));
            setUnifiedInsights(mappedInsights);
          }

          // Set weekly insights
          if (weeklyResult) {
            setWeeklyInsightData(weeklyResult);
          } else {
            // Fallback: check if user has check-ins
            try {
              // getCheckIns returns an array directly
              const checkIns = await wellnessService.getCheckIns({ limit: 5 });
              const checkInsArray = Array.isArray(checkIns) ? checkIns : [];
              if (checkInsArray.length === 0) {
                setWeeklyInsightData(createBaselineInsights());
              } else if (checkInsArray.length < 5) {
                setWeeklyInsightData(createStarterInsights(checkInsArray));
              }
            } catch (error) {
              console.error("Error fetching check-ins for fallback:", error);
              setWeeklyInsightData(createBaselineInsights());
            }
          }
        }
      } catch (error) {
        console.error("Error loading insights:", error);
        // Set baseline insights as fallback on error
        setWeeklyInsightData(createBaselineInsights());
        setUnifiedInsights([]);
      }
    };

    loadInsights();
  }, [user?.id, user?.teamId, user?.isNewUser]);

  const getInsightIcon = (actionType: string) => {
    switch (actionType) {
      case "focus":
        return <Clock className="h-5 w-5" />;
      case "connect":
        return <TrendingUp className="h-5 w-5" />;
      case "reflect":
        return <Star className="h-5 w-5" />;
      case "break":
        return <Check className="h-5 w-5" />;
      default:
        return <Clock className="h-5 w-5" />;
    }
  };

  const getPriorityBadge = (priority: string) => {
    switch (priority) {
      case "high":
        return <Badge variant="destructive">High Priority</Badge>;
      case "medium":
        return <Badge variant="secondary">Medium Priority</Badge>;
      case "low":
        return <Badge variant="outline">Low Priority</Badge>;
      default:
        return null;
    }
  };

  const getActionButton = (insight: InsightData) => {
    if (insight.title === "Schedule Focus Time") {
      return <ScheduleFocusTime buttonText="Schedule Now" />;
    }

    return (
      <Button
        size="sm"
        variant="secondary"
        onClick={() => handleTakeAction(insight)}
      >
        Take Action
      </Button>
    );
  };

  const handleTakeAction = async (insight: InsightData) => {
    console.log("Taking action on:", insight.title);
    // Handle the action based on the insight type
    switch (insight.actionType) {
      case "focus":
        // Focus time action
        console.log("Scheduling focus time");
        break;
      case "connect":
        // Connect action - Send notification to all team members
        console.log("Connecting with team members");
        break;
      case "reflect":
        // Redirect to profile page with goals tab
        console.log("Redirecting to profile with goals tab");
        navigate("/profile", {
          state: { activeTab: "goals" },
          replace: true,
        });
        break;
      case "break":
        // Break action
        console.log("Taking a break");
        break;
      default:
        break;
    }
  };

  const handleUnifiedAction = async (insight: any) => {
    console.log("Taking action on unified insight:", insight.title);
    
    // If there's an actionable insight, process it
    if (insight.actionable) {
      // Map actionable insight to appropriate action
      switch (insight.actionable.category) {
        case "wellbeing":
          // Navigate to check-in or wellness page
          navigate("/check-in");
          break;
        case "productivity":
          // Could open schedule or focus time dialog
          console.log("Productivity action");
          break;
        case "collaboration":
          // Navigate to team page
          navigate("/team");
          break;
        case "growth":
          // Navigate to goals
          navigate("/profile", {
            state: { activeTab: "goals" },
            replace: true,
          });
          break;
      }
    }
  };

  // Show empty state for new users or users without check-ins
  if (!weeklyInsightData || user?.isNewUser) {
    return (
      <div className="space-y-6">
        <Card className="shadow-sm">
          <CardHeader>
            <div className="flex items-center gap-2">
              <Lightbulb className="h-5 w-5 text-amber-500" />
              <CardTitle>Weekly Insights</CardTitle>
            </div>
            <CardDescription>Analysis of your recent check-ins</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="text-center py-12">
              <Lightbulb className="h-12 w-12 text-muted-foreground mx-auto mb-4 opacity-50" />
              <h3 className="text-lg font-medium mb-2">No insights yet</h3>
              <p className="text-muted-foreground max-w-md mx-auto">
                Complete your first check-in to start building insights about your work patterns and wellbeing.
              </p>
              <Button 
                className="mt-6" 
                onClick={() => window.location.href = '/check-in'}
              >
                Take Your First Check-in
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Show loading state while insights are being loaded
  if (!weeklyInsightData) {
    return (
      <div className="space-y-6">
        <EnhancedInsightsPanel className="mb-6" />
        <Card className="shadow-sm">
          <CardContent className="p-8">
            <div className="text-center">
              <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full mx-auto mb-4"></div>
              <p className="text-muted-foreground">Loading your personalized insights...</p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Prepare data for pie chart
  const pieData = [
    {
      name: "Positive",
      value: weeklyInsightData.tone.positive,
      color: "#22c55e",
    },
    {
      name: "Neutral",
      value: weeklyInsightData.tone.neutral,
      color: "#3b82f6",
    },
    {
      name: "Negative",
      value: weeklyInsightData.tone.negative,
      color: "#f97316",
    },
  ];

  return (
    <div className="space-y-6">
      {/* Enhanced Pattern Analysis */}
      <EnhancedInsightsPanel className="mb-6" />
      
      {/* Basic Weekly Insights Panel */}
      <Card className="shadow-sm">
        <CardHeader>
          <div className="flex items-center gap-2">
            <Lightbulb className="h-5 w-5 text-amber-500" />
            <CardTitle>Weekly Insights</CardTitle>
          </div>
          <CardDescription>Analysis of your recent check-ins</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 md:gap-6">
            <div>
              <h4 className="text-sm font-medium mb-2">Reflection Tone</h4>
              <div className="h-40">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={pieData}
                      cx="50%"
                      cy="50%"
                      innerRadius={40}
                      outerRadius={70}
                      paddingAngle={2}
                      dataKey="value"
                    >
                      {pieData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </div>

            <div className="space-y-4">
              <div>
                <h4 className="text-sm font-medium mb-2">Top Themes</h4>
                <div className="flex flex-wrap gap-2">
                  {weeklyInsightData.topThemes.map((theme) => (
                    <Badge key={theme.name} variant="secondary">
                      {theme.name} ({theme.count})
                    </Badge>
                  ))}
                </div>
              </div>

              <div>
                <h4 className="text-sm font-medium mb-2">Weekly Changes</h4>
                <div className="space-y-2">
                  <div className="flex justify-between items-center">
                    <span className="text-sm">Energy</span>
                    <div
                      className={`flex items-center gap-1 ${
                        weeklyInsightData.weeklyChanges.energy > 0
                          ? "text-green-600"
                          : "text-red-600"
                      }`}
                    >
                      {weeklyInsightData.weeklyChanges.energy > 0 ? (
                        <TrendingUp size={16} />
                      ) : (
                        <TrendingUp size={16} className="rotate-180" />
                      )}
                      <span>
                        {Math.abs(weeklyInsightData.weeklyChanges.energy)}%
                      </span>
                    </div>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm">Focus</span>
                    <div
                      className={`flex items-center gap-1 ${
                        weeklyInsightData.weeklyChanges.focus > 0
                          ? "text-green-600"
                          : "text-red-600"
                      }`}
                    >
                      {weeklyInsightData.weeklyChanges.focus > 0 ? (
                        <TrendingUp size={16} />
                      ) : (
                        <TrendingUp size={16} className="rotate-180" />
                      )}
                      <span>
                        {Math.abs(weeklyInsightData.weeklyChanges.focus)}%
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Focus Patterns, Energy Trend, and Upcoming Activities */}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-3 md:gap-6">
        <Card>
          <CardHeader className="pb-2">
            <div className="flex items-center gap-2">
              <div className="p-2 bg-amber-100 rounded-full">
                <Lightbulb className="h-5 w-5 text-amber-600" />
              </div>
              <CardTitle className="text-lg">Focus Patterns</CardTitle>
            </div>
          </CardHeader>
          <CardContent>
            <p className="text-muted-foreground">
              {weeklyInsightData.focusPatterns.peakTimeRange}
              <br />
              {weeklyInsightData.focusPatterns.recommendation}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <div className="flex items-center gap-2">
              <div className="p-2 bg-green-100 rounded-full">
                <TrendingUp className="h-5 w-5 text-green-600" />
              </div>
              <CardTitle className="text-lg">Energy Trend</CardTitle>
            </div>
          </CardHeader>
          <CardContent>
            <p className="text-muted-foreground">
              {weeklyInsightData.energyTrend.message}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <div className="flex items-center gap-2">
              <div className="p-2 bg-blue-100 rounded-full">
                <Calendar className="h-5 w-5 text-blue-600" />
              </div>
              <CardTitle className="text-lg">Upcoming Activities</CardTitle>
            </div>
          </CardHeader>
          <CardContent>
            <p className="text-muted-foreground">
              {weeklyInsightData.upcomingActivities.message}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Weekly Summary */}
      <Card>
        <CardHeader>
          <CardTitle>Weekly Summary</CardTitle>
          <CardDescription>
            AI-generated insights from your check-ins
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {weeklyInsightData.weeklySummary.text.map((paragraph, index) => (
            <p key={index} className="text-muted-foreground">
              {paragraph}
            </p>
          ))}
          <div className="mt-4 p-4 bg-muted rounded-md">
            <p className="font-medium">Recommendation:</p>
            <p className="text-muted-foreground">
              {weeklyInsightData.weeklySummary.recommendation}
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Unified Priority Insights - Data-Driven Recommendations */}
      {unifiedInsights.length > 0 && (
        <Card className="border-2 border-primary/20 shadow-lg">
          <CardHeader>
            <div className="flex items-center gap-2">
              <Lightbulb className="h-6 w-6 text-primary" />
              <div>
                <CardTitle>Your Priority Actions</CardTitle>
                <CardDescription>
                  Coherent insights from all your data - individual patterns, team context, and AI analysis
                </CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {unifiedInsights.map((insight) => (
                <div
                  key={insight.id}
                  className={`p-4 rounded-lg border-l-4 ${
                    insight.priority === 'critical'
                      ? 'border-red-500 bg-red-50 dark:bg-red-950/20'
                      : insight.priority === 'high'
                      ? 'border-orange-500 bg-orange-50 dark:bg-orange-950/20'
                      : insight.priority === 'medium'
                      ? 'border-blue-500 bg-blue-50 dark:bg-blue-950/20'
                      : 'border-gray-300 bg-gray-50 dark:bg-gray-900/20'
                  }`}
                >
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <h4 className="font-semibold text-base">{insight.title}</h4>
                        <Badge
                          variant={insight.priority === 'critical' ? 'destructive' : 'secondary'}
                          className="text-xs"
                        >
                          {insight.priority}
                        </Badge>
                        <Badge variant="outline" className="text-xs">
                          {insight.category}
                        </Badge>
                      </div>
                      <p className="text-sm text-muted-foreground mb-2">
                        {insight.description}
                      </p>
                      <p className="text-xs text-muted-foreground italic">
                        {insight.reasoning}
                      </p>
                      {insight.teamContext && (
                        <div className="mt-2 flex items-center gap-1 text-xs text-blue-600 dark:text-blue-400">
                          <TrendingUp className="h-3 w-3" />
                          <span>
                            {insight.teamContext.isTeamwide
                              ? 'Team-wide insight'
                              : 'Considers your team context'}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 text-xs text-muted-foreground">
                    <span>Sources:</span>
                    {insight.sources.map((source) => (
                      <Badge key={source} variant="outline" className="text-xs">
                        {source.replace(/_/g, ' ')}
                      </Badge>
                    ))}
                  </div>
                  {insight.actionable && (
                    <div className="mt-3 flex justify-end">
                      <Button size="sm" onClick={() => handleUnifiedAction(insight)}>
                        Take Action
                      </Button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Action Items */}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 md:gap-6">
        {insights.map((insight) => (
          <Card key={insight.id} className="shadow-sm">
            <CardHeader className="pb-2">
              <div className="flex justify-between items-start">
                <div className="flex items-center gap-2">
                  {getInsightIcon(insight.actionType)}
                  <CardTitle className="text-base">{insight.title}</CardTitle>
                </div>
                {getPriorityBadge(insight.priority)}
              </div>
              <CardDescription>{insight.description}</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex justify-end">{getActionButton(insight)}</div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}
